import { interpolateViridis } from 'd3-scale-chromatic';
import React, { useEffect, useRef } from 'react';
import uPlot from 'uplot';
import { useSessionContext } from './SessionContext';

const LIGHT_GRAY = 'rgb(199, 199, 201)';
const GRAY = 'rgb(142, 142, 147)';

function scaleGradient(u) {
  const minPos = u.valToPos(-100, 'y', true);
  const maxPos = u.valToPos(100, 'y', true);

  const can = document.createElement('canvas');
  const ctx = can.getContext('2d');
  const gradient = ctx.createLinearGradient(0, minPos, 0, maxPos);

  for (let t = 0; t <= 1; t += 0.1) {
    gradient.addColorStop(t, interpolateViridis(1 - t));
  }

  return gradient;
}

export default function Timeseries() {
  const {
    selectedHexagon, dates, selectedDateIndex, isMobile
  } = useSessionContext();
  const chartRef = useRef(null);
  const uPlotInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !dates || !selectedHexagon) {
      return () => {
        if (uPlotInstance.current) uPlotInstance.current.destroy();
      };
    }

    const opts = {
      focus: false,
      legend: {
        show: false
      },
      cursor: {
        focus: {
          prox: 20,
          alpha: 1
        }
      },
      scales: {
        y: {
          min: -100,
          max: 100
        }
      },
      hooks: {
        drawSeries: [
          (u) => {
            const { ctx } = u;
            ctx.save();
            const epochs = u.data[0];

            const xMin = u.valToPos(epochs[0], 'x', true);
            const yMin = u.valToPos(-100, 'y', true);
            const xMax = u.valToPos(epochs[epochs.length - 1], 'x', true);
            const yMax = u.valToPos(100, 'y', true);
            console.log(yMin, yMax);

            ctx.strokeStyle = GRAY;
            ctx.lineWidth = 4;

            ctx.beginPath();
            ctx.moveTo(xMin, yMax - devicePixelRatio);
            ctx.lineTo(xMin, yMin + devicePixelRatio);
            ctx.stroke();

            ctx.beginPath();
            ctx.moveTo(xMax, yMax - devicePixelRatio);
            ctx.lineTo(xMax, yMin + devicePixelRatio);
            ctx.stroke();

            ctx.beginPath();
            ctx.moveTo(xMin, yMin);
            ctx.lineTo(xMax, yMin);
            ctx.stroke();

            ctx.beginPath();
            ctx.moveTo(xMin, yMax);
            ctx.lineTo(xMax, yMax);
            ctx.stroke();

            ctx.restore();
          }
        ]
      },
      axes: [
        {
          stroke: LIGHT_GRAY,
          ticks: GRAY,
          show: !isMobile,
          grid: {
            stroke: GRAY,
            width: 1 / devicePixelRatio,
          },
        },
        {
          stroke: LIGHT_GRAY,
          ticks: GRAY,
          grid: {
            stroke: GRAY,
            width: 1 / devicePixelRatio,
          }
        }],
      width: isMobile ? window.innerWidth - 32 : 284,
      height: isMobile ? 120 : 200,
      series: [
        {},
        {
          spanGaps: false,
          width: 6 / devicePixelRatio,
          stroke: scaleGradient,
        },
        {
          show: true,
          points: {
            fill: 'rgb(161, 54, 157)',
            stroke: 'rgb(161, 54, 157)',
            show: true,
          },
          spanGaps: false,
          width: 8 / devicePixelRatio,
          stroke: 'rgb(0, 0, 255)',
        }
      ]
    };

    const timeseries = selectedHexagon.timeline.map((frame) => (frame ? frame.wli : null));

    const focusDot = timeseries.map((wli, index) => (
      index === selectedDateIndex ? wli : null
    ));

    uPlotInstance.current = uPlot(
      opts,
      [
        dates.map((date) => new Date(date)).map((date) => date.getTime() / 1000),
        timeseries,
        focusDot
      ],
      chartRef.current
    );

    return () => {
      if (uPlotInstance.current) uPlotInstance.current.destroy();
    };
  }, [chartRef, dates, selectedHexagon, isMobile]);

  useEffect(() => {
    if (!uPlotInstance.current) return;
    const [epochs, timeseries] = uPlotInstance.current.data;
    const focusDot = timeseries.map((wli, index) => (
      index === selectedDateIndex ? wli : null
    ));
    uPlotInstance.current.setData([epochs, timeseries, focusDot], false);
    uPlotInstance.current.setScale('y', { min: -100, max: 100 });
  }, [selectedDateIndex]);

  return (
    <div ref={chartRef} />
  );
}
